import React, { useEffect, useState, useRef } from 'react';
import { Container, Header, Card, Label, Image, Button, Icon } from 'semantic-ui-react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getBrands, formatUrlPart, SITE_BASE_URL } from '../services/api';
import TopSection from './TopSection';
import DisclaimerLink from './DisclaimerLink';
import '../index.css';

const baseUrl = SITE_BASE_URL.replace(/\/$/, '');

const HomePage = () => {
  // State to hold topBrands and allBrands
  const [brandData, setBrandData] = useState({ topBrands: [], allBrands: [] });
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedBrands, setExpandedBrands] = useState([]); // Shared expand state per brand
  const [visibleModelsByBrand, setVisibleModelsByBrand] = useState({}); // Visible models per section-brand
  const labelGroupRefs = useRef({}); // Refs for DOM calculations
  const navigate = useNavigate();

  // Fetch brands on mount
  useEffect(() => {
    getBrands()
      .then((data) => setBrandData(data))
      .catch((err) => console.error(err));
  }, []);

  // Calculate visible models for each brand card based on DOM layout
  useEffect(() => {
    if (brandData.topBrands.length > 0 && brandData.allBrands.length > 0) {
      const newVisibleModels = {};

      const processBrands = (brands, section) => {
        brands.forEach((brand) => {
          const labelGroup = labelGroupRefs.current[`${section}-${brand._id}`];
          if (labelGroup) {
            const labels = labelGroup.querySelectorAll('.label');
            if (labels.length > 0) {
              const rowHeights = Array.from(labels).map((label) => label.offsetTop);
              const uniqueRows = [...new Set(rowHeights)].sort((a, b) => a - b);
              if (uniqueRows.length > 2) {
                const secondRowTop = uniqueRows[1];
                const visibleLabels = Array.from(labels).filter((label) => label.offsetTop <= secondRowTop);
                newVisibleModels[`${section}-${brand._id}`] = visibleLabels.map((label) => label.textContent);
              } else {
                newVisibleModels[`${section}-${brand._id}`] = [...new Set(brand.transcripts.map((tx) => tx.model))];
              }
            }
          }
        });
      };

      processBrands(brandData.topBrands, 'top');
      processBrands(brandData.allBrands, 'all');
      setVisibleModelsByBrand(newVisibleModels);
    }
  }, [brandData]);

  // Handle search navigation
  const handleSearch = () => {
    if (searchQuery.trim() !== '') {
      navigate(`/search/${formatUrlPart(searchQuery)}`);
    }
  };

  // Toggle expansion of a brand card
  const toggleCardExpansion = (brandId, e) => {
    e.stopPropagation();
    setExpandedBrands((prev) =>
      prev.includes(brandId) ? prev.filter((id) => id !== brandId) : [...prev, brandId]
    );
  };

  // Render a single brand card
  const renderBrandCard = (brand, section) => {
    const uniqueModels = [...new Set(brand.transcripts.map((tx) => tx.model))];
    const isExpanded = expandedBrands.includes(brand._id);
    const displayedModels = isExpanded ? uniqueModels : visibleModelsByBrand[`${section}-${brand._id}`] || uniqueModels;
    const hasMore =
      visibleModelsByBrand[`${section}-${brand._id}`] && visibleModelsByBrand[`${section}-${brand._id}`].length < uniqueModels.length;

    return (
      <Card
        key={`${section}-${brand._id}`} // Unique key per section and brand
        raised
        className="brand-card custom-card"
        style={{ margin: '10px', backgroundColor: '#f9f9f9', border: '1px solid #ddd' }}
        onClick={() => navigate(`/brand/${formatUrlPart(brand._id)}`)}
      >
        <Card.Content>
          {brand.logoUrl && <Image src={brand.logoUrl} wrapped ui={false} style={{ marginBottom: '10px' }} />}
          <Card.Header>
            <Link
              to={`/brand/${formatUrlPart(brand._id)}`}
              style={{ color: '#007bff' }}
              onClick={(e) => e.stopPropagation()}
            >
              {brand.originalBrand}
            </Link>
          </Card.Header>
          <Card.Meta style={{ color: '#555' }}>{brand.count} Articles</Card.Meta>
          <Card.Description>
            <strong>Models:</strong>
            <div className="model-labels" ref={(el) => (labelGroupRefs.current[`${section}-${brand._id}`] = el)}>
              {displayedModels.map((model, idx) => (
                <Link
                  to={`/brand/${formatUrlPart(brand._id)}/model/${formatUrlPart(model)}`}
                  key={idx}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Label basic color="blue" className="accent-label" style={{ margin: '5px 2px' }}>
                    {model}
                  </Label>
                </Link>
              ))}
            </div>
            {hasMore && !isExpanded && (
              <Button
                basic
                fluid
                size="tiny"
                onClick={(e) => toggleCardExpansion(brand._id, e)}
                style={{ marginTop: '10px' }}
              >
                <Icon name="arrow down" /> View More
              </Button>
            )}
            {isExpanded && (
              <Button
                basic
                fluid
                size="tiny"
                onClick={(e) => toggleCardExpansion(brand._id, e)}
                style={{ marginTop: '10px' }}
              >
                <Icon name="arrow up" /> View Less
              </Button>
            )}
          </Card.Description>
        </Card.Content>
      </Card>
    );
  };

  const canonicalUrl = baseUrl;
  const title = 'Blue Compass RV Troubleshooting - Browse Brands & Models';
  const description = 'Browse resolved RV support transcripts by brand and model. Learn from thousands of live calls with certified Blue Compass RV technicians.';
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Blue Compass RV Technical Support',
    url: baseUrl,
    description: 'Resolved RV troubleshooting transcripts from certified technicians. Search by brand and model.',
    publisher: { '@type': 'Organization', name: 'Blue Compass RV' },
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${baseUrl}/bcrvlogo.png`} />
        <meta property="og:site_name" content="Blue Compass RV Technical Support" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      </Helmet>

      <TopSection searchQuery={searchQuery} setSearchQuery={setSearchQuery} handleSearch={handleSearch} />
      <main>
      <Container className="container" style={{ padding: '20px', backgroundColor: '#ffffff' }}>
        <p style={{ marginBottom: '20px' }}>
          <DisclaimerLink />
        </p>
        {/* Top Brands Section */}
        <Header as="h2" textAlign="center" style={{ marginTop: '40px', marginBottom: '30px', color: '#000' }}>
          Top Brands
        </Header>
        <Card.Group itemsPerRow={3} stackable style={{ margin: '0 -10px' }}>
          {brandData.topBrands.map((brand) => renderBrandCard(brand, 'top'))}
        </Card.Group>

        {/* All Brands Section */}
        <Header as="h2" textAlign="center" style={{ marginTop: '40px', marginBottom: '30px', color: '#000' }}>
          All Brands
        </Header>
        <Card.Group itemsPerRow={3} stackable style={{ margin: '0 -10px' }}>
          {brandData.allBrands.map((brand) => renderBrandCard(brand, 'all'))}
        </Card.Group>
      </Container>
      </main>
    </>
  );
};

export default HomePage;